
import React, { useState, useEffect, createContext, useContext } from 'react';
import { 
    auth, 
    db, 
    functions, 
    increment, 
    arrayUnion, 
    arrayRemove, 
    serverTimestamp 
} from './firebase';
import { UserProfile, AuthContextType, Puzzle, GroupGame } from './types';
import { STRIPE_PRICE_IDS } from './stripeConfig';
import { Capacitor } from '@capacitor/core';
import { sendDirectNotification } from './notificationUtils';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};

export const playSound = (soundUrl: string) => {
    if (!soundUrl || soundUrl === 'None') return;
    const audio = new Audio(soundUrl);
    audio.play().catch(() => {}); 
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<any | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [usersLoading, setUsersLoading] = useState(true);
    const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
    const [groups, setGroups] = useState<GroupGame[]>([]);
    const [sentGroupRequests] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!auth) {
            setLoading(false);
            setUsersLoading(false);
            return;
        }
        const unsubscribe = auth.onAuthStateChanged(async (u: any) => {
            console.log("Auth State Changed:", u ? "User Logged In" : "User Logged Out");
            setUser(u);
            if (u) {
                // Mark online initially if not appearing offline
                if (db) {
                    const userDoc = await db.collection('users').doc(u.uid).get();
                    const data = userDoc.data();
                    const shouldBeOnline = data?.appearOffline ? false : true;

                    db.collection('users').doc(u.uid).update({
                        isOnline: shouldBeOnline,
                        lastSeen: serverTimestamp()
                    }).catch((e: any) => console.warn("Firestore status update failed (likely rules):", e.message));
                }
            } else {
                setUserProfile(null);
                setLoading(false);
                setUsersLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!user || !db) {
            setAllUsers([]);
            setGroups([]);
            if (user && !db) {
                console.error("User logged in but Firestore is not available. Stopping loading state.");
                setLoading(false);
                setUsersLoading(false);
            }
            return;
        }

        const unsubUsers = db.collection('users').onSnapshot((snapshot: any) => {
            const users = snapshot.docs.map((doc: any) => ({ uid: doc.id, ...doc.data() } as UserProfile));
            setAllUsers(users);
            
            const myProfile = users.find((u: any) => u.uid === user.uid);
            if (myProfile) {
                if ((user.email?.toLowerCase() === 'candrewgoddard@gmail.com' || user.email?.toLowerCase() === 'whatwasitltd@gmail.com') && !myProfile.isAdmin) {
                    myProfile.isAdmin = true;
                    db.collection('users').doc(user.uid).update({ isAdmin: true }).catch(console.error);
                }
                setUserProfile(myProfile);
            } else {
                console.warn("User profile not found in Firestore snapshot. Attempting to repair by creating a basic profile...");
                
                // Instead of deleting the auth account (which breaks manually created admin accounts),
                // we will just create a basic profile document for them.
                const newProfile: any = {
                    uid: user.uid,
                    displayName: user.displayName || user.email?.split('@')[0] || 'User',
                    username: 'u_' + Math.floor(Math.random() * 1000000),
                    email: user.email || '',
                    photoURL: user.photoURL || 'https://cdn-icons-png.flaticon.com/512/847/847969.png',
                    dob: '01/01/2000',
                    score: 0, clues: 3, following: [], followers: [], solvedPuzzles: [],
                    isPrivate: false, requestType: 'follow',
                    primaryColor: 'hsl(259, 55%, 20%)', postColor: 'hsl(259, 55%, 20%)',
                    correctGuessSound: 'Triumph', wrongGuessSound: 'Buzz',
                    uiClickSound: 'Pop', uiNavigationSound: 'None', uiNotificationSound: 'Bell Tower',
                    uiClueRevealSound: 'Magic', backgroundMusic: 'None',
                    enableShimmerEffect: true, aiCredits: 3, hasCompletedOnboarding: false, isOnline: true,
                    isAdmin: user.email?.toLowerCase() === 'candrewgoddard@gmail.com' || user.email?.toLowerCase() === 'whatwasitltd@gmail.com',
                    createdAt: serverTimestamp()
                };
                
                db.collection('users').doc(user.uid).set(newProfile)
                    .then(() => {
                        setUserProfile(newProfile as UserProfile);
                    })
                    .catch((err: any) => {
                        console.error("Failed to repair profile:", err);
                        auth.signOut();
                    });
            }
            setUsersLoading(false);
            setLoading(false);
        }, (err: any) => {
            console.error("Error fetching users snapshot:", err);
            setUsersLoading(false);
            setLoading(false);
        });

        const unsubGroups = db.collection('groups').onSnapshot((snapshot: any) => {
            const loadedGroups = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as GroupGame));
            setGroups(loadedGroups);
        }, (err: any) => {
            console.error("Error fetching groups snapshot:", err);
        });

        return () => {
            unsubUsers();
            unsubGroups();
        };
    }, [user]);

    useEffect(() => {
        if (!loading && !usersLoading && user && !userProfile) {
            // We used to have a zombie state deletion here, but it triggers on network errors (400 Bad Request).
            // We'll rely on the snapshot listener's success callback to log out if the profile is truly missing.
        }
    }, [user, userProfile, loading, usersLoading]);

    // Presence Heartbeat & Tab Close Logic
    useEffect(() => {
        if (!user || !userProfile) return;

        const updateStatus = (online: boolean) => {
            const status = userProfile.appearOffline ? false : online;
            db.collection('users').doc(user.uid).update({
                isOnline: status,
                lastSeen: serverTimestamp()
            }).catch(() => {});
        };

        // Initial update
        updateStatus(true);

        // Heartbeat every 2 minutes
        const heartbeat = setInterval(() => {
            updateStatus(true);
        }, 120000);

        // Tab close listener
        const handleUnload = () => {
            // Best effort update
            db.collection('users').doc(user.uid).update({
                isOnline: false,
                lastSeen: serverTimestamp()
            });
        };

        window.addEventListener('beforeunload', handleUnload);

        return () => {
            clearInterval(heartbeat);
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, [user, userProfile?.appearOffline]);

    const login = async (email: string, password: string) => {
        if (!auth || !db) throw new Error("Firebase not loaded. Please check your connection.");
        
        // Permanent Ban Check
        const banCheck = await db.collection('banned_list').doc(email.toLowerCase()).get();
        if (banCheck.exists) {
            throw new Error("This account has been permanently banned from What Was It?");
        }

        await auth.signInWithEmailAndPassword(email, password);
    };

    const signup = async (email: string, password: string, displayName: string, dob: string) => {
        if (!auth || !db) throw new Error("Firebase not loaded. Please check your connection.");

        // Permanent Ban Check
        const emailBanCheck = await db.collection('banned_list').doc(email.toLowerCase()).get();
        if (emailBanCheck.exists) {
            throw new Error("This email has been permanently banned from joining the app.");
        }

        // Auto-generate a unique handle based on display name and a random suffix
        const sanitizedBase = displayName.toLowerCase().replace(/[^a-z0-9]/g, '') || 'user';
        const randomSuffix = Math.floor(1000 + Math.random() * 9000).toString();
        const generatedUsername = `${sanitizedBase}${randomSuffix}`;

        const usernameBanCheck = await db.collection('banned_list').doc(`u_${generatedUsername.toLowerCase()}`).get();
        if (usernameBanCheck.exists) {
            throw new Error("This username is unavailable due to a permanent ban.");
        }

        // Check uniqueness is handled by Firestore security rules or we assume low collision with suffix
        // Ideally we would retry if it fails, but for this implementation we assume success.
        
        const cred = await auth.createUserWithEmailAndPassword(email, password);
        if (cred.user) {
            const uid = cred.user.uid;
            const newUser: UserProfile = {
                uid, displayName, username: generatedUsername,
                email: email.toLowerCase(),
                photoURL: 'https://cdn-icons-png.flaticon.com/512/847/847969.png',
                dob, score: 0, clues: 3, following: [], followers: [], solvedPuzzles: [],
                isPrivate: false, requestType: 'follow',
                primaryColor: 'hsl(259, 55%, 20%)', postColor: 'hsl(259, 55%, 20%)',
                correctGuessSound: 'Triumph', wrongGuessSound: 'Buzz',
                uiClickSound: 'Pop', uiNavigationSound: 'None', uiNotificationSound: 'Bell Tower',
                uiClueRevealSound: 'Magic', backgroundMusic: 'None',
                enableShimmerEffect: true, aiCredits: 3, hasCompletedOnboarding: false, isOnline: true,
                isAdmin: email.toLowerCase() === 'candrewgoddard@gmail.com' || email.toLowerCase() === 'whatwasitltd@gmail.com',
                createdAt: serverTimestamp()
            };
            const batch = db.batch();
            batch.set(db.collection('users').doc(uid), newUser);
            batch.set(db.collection('usernames').doc(generatedUsername), { uid });
            try {
                await batch.commit();
            } catch (err) {
                console.error("Failed to create user profile in Firestore. Reverting auth...", err);
                await cred.user.delete().catch(() => auth.signOut());
                throw err;
            }
        }
    };

    const logout = async () => {
        console.log("Initiating Logout Flow...");
        if (!auth) return;
        const uid = user?.uid;
        
        // 1. Immediately Clear Local UI States
        setUser(null);
        setUserProfile(null);
        setLoading(false); // Force loading false so App.tsx can redirect
        setUsersLoading(false);

        try {
            if (uid) {
                // 2. Non-blocking cleanup of online status
                db.collection('users').doc(uid).update({ 
                    isOnline: false, 
                    lastSeen: serverTimestamp() 
                }).catch((e: any) => {
                    console.error("Firestore LOGOUT error (Permission Denied?):", e.message);
                });
            }
            // 3. Official Firebase SignOut
            await auth.signOut();
            console.log("Logout successful.");
        } catch (error: any) {
            console.error("Firebase SignOut error:", error.message);
            // Even if it fails, local state is already null, so the app will look logged out.
        }
    };

    const deleteAccount = async () => {
        if (!user || !userProfile || !db) return;
        const uid = user.uid;
        const username = userProfile.username;

        try {
            // 1. Clean up Firestore Data (Client side cleanup best effort)
            // Note: Ideally cloud functions handle cascading deletes to prevent orphaned posts
            const batch = db.batch();
            
            // Delete User Profile
            batch.delete(db.collection('users').doc(uid));
            
            // Delete Username Reservation
            if (username) {
                batch.delete(db.collection('usernames').doc(username));
            }
            
            await batch.commit();

            // 2. Delete Auth Account (Must happen last)
            // Requires recent authentication. If failed, user must re-login.
            await user.delete();
            
            // Reset State
            setUser(null);
            setUserProfile(null);
            setLoading(false);

        } catch (error: any) {
            console.error("Delete Account Error:", error);
            if (error.code === 'auth/requires-recent-login') {
                throw new Error("Please log out and log back in to verify your identity before deleting your account.");
            }
            throw error;
        }
    };

    const resetPassword = async (email: string) => { 
        if (!auth) throw new Error("Firebase not loaded.");
        await auth.sendPasswordResetEmail(email); 
    };
    const updateSettings = async (settings: Partial<UserProfile>) => { if (user && db) await db.collection('users').doc(user.uid).update(settings); };
    const updateUsername = async (newUsername: string) => {
        if (!user || !userProfile || !db) return;
        const lowerUsername = newUsername.toLowerCase().trim();
        const doc = await db.collection('usernames').doc(lowerUsername).get();
        if (doc.exists && doc.data()?.uid !== user.uid) throw new Error("Username taken.");
        const batch = db.batch();
        if (userProfile.username) batch.delete(db.collection('usernames').doc(userProfile.username.toLowerCase()));
        batch.set(db.collection('usernames').doc(lowerUsername), { uid: user.uid });
        batch.update(db.collection('users').doc(user.uid), { username: lowerUsername });
        await batch.commit();
    };

    const addPoints = async (p: number) => user && db && db.collection('users').doc(user.uid).update({ score: increment(p) });
    const addPointsToUser = async (uid: string, p: number) => db && db.collection('users').doc(uid).update({ score: increment(p) });
    const updateScore = async (p: number, puzzle: Puzzle) => {
        if (!user || !db || !userProfile) return;
        
        const oldScore = userProfile.score;
        const newScore = oldScore + p;
        
        await db.collection('users').doc(user.uid).update({ 
            score: increment(p), 
            solvedPuzzles: arrayUnion(puzzle.id) 
        });

        // Leaderboard pass check
        // Find users that this user just passed
        const usersPassed = allUsers.filter(u => 
            u.uid !== userProfile.uid && 
            u.score >= oldScore && 
            u.score < newScore
        );

        usersPassed.forEach(u => {
            sendDirectNotification(u.uid, {
                type: 'leaderboard_pass',
                text: `just passed you on the leaderboard!`,
                fromUser: {
                    id: userProfile.uid,
                    name: userProfile.displayName,
                    avatar: userProfile.photoURL
                }
            });
        });

        // Milestone check (every 100 points)
        if (Math.floor(newScore / 100) > Math.floor(oldScore / 100)) {
            const milestone = Math.floor(newScore / 100) * 100;
            sendDirectNotification(user.uid, {
                type: 'system',
                text: `🎉 Milestone! You reached ${milestone} total points!`,
                fromUser: {
                    id: 'system',
                    name: 'What Was It?',
                    avatar: 'https://cdn-icons-png.flaticon.com/512/3112/3112946.png'
                }
            });
        }
    };
    const deductPoints = async (p: number) => user && db && db.collection('users').doc(user.uid).update({ score: increment(-p) });
    const useClue = async (pid: string, idx: number) => userProfile && db && db.collection('users').doc(userProfile.uid).update({ clues: increment(-1), [`revealedClues.${pid}`]: arrayUnion(idx) });
    const addFreeClue = async () => user && db && db.collection('users').doc(user.uid).update({ clues: increment(1) });
    const addCluesAfterNativeIAP = async (amount: number) => {
        if (user && db) {
            await db.collection('users').doc(user.uid).update({ clues: increment(amount) });
            return true;
        }
        return false;
    };
    const toggleFollow = async (tid: string) => {
        if (!userProfile || !db) return;
        const isFollowing = userProfile.following.includes(tid);
        const batch = db.batch();
        const meRef = db.collection('users').doc(userProfile.uid);
        const themRef = db.collection('users').doc(tid);
        if (isFollowing) { batch.update(meRef, { following: arrayRemove(tid) }); batch.update(themRef, { followers: arrayRemove(userProfile.uid) }); }
        else { batch.update(meRef, { following: arrayUnion(tid) }); batch.update(themRef, { followers: arrayUnion(userProfile.uid) }); }
        await batch.commit();
    };
    const toggleBlockUser = async (tid: string) => {
        if (!userProfile || !db) return;
        const isBlocked = userProfile.blockedUsers?.includes(tid);
        if (isBlocked) await db.collection('users').doc(userProfile.uid).update({ blockedUsers: arrayRemove(tid) });
        else {
            await db.collection('users').doc(userProfile.uid).update({ blockedUsers: arrayUnion(tid) });
            if (userProfile.following.includes(tid)) await toggleFollow(tid);
        }
    };

    const createGroup = async (d: any) => {
        if (!userProfile || !db) return;
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();
        const res = await db.collection('groups').add({ ...d, creatorId: userProfile.uid, members: [userProfile.uid], admins: [userProfile.uid], scores: { [userProfile.uid]: 0 }, solvedPuzzles: {}, joinRequests: [], inviteCode: code });
        return res.id;
    };
    const updateGroup = async (gid: string, d: any) => db && db.collection('groups').doc(gid).update(d);
    const deleteGroup = async (gid: string) => db && db.collection('groups').doc(gid).delete();
    const joinGame = async (c: string) => {
        if (!userProfile || !db) return;
        const snap = await db.collection('groups').where('inviteCode', '==', c).get();
        const group = snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() } as any;
        if (group) {
            if (group.members.includes(userProfile.uid)) return;
            if (group.privacy === 'ask-to-join') { 
                await db.collection('groups').doc(group.id).update({ joinRequests: arrayUnion(userProfile.uid) }); 
                // Notify admins
                group.admins.forEach((adminId: string) => {
                    sendDirectNotification(adminId, {
                        type: 'group_join_request',
                        text: `requested to join your group "${group.name}".`,
                        fromUser: {
                            id: userProfile.uid,
                            name: userProfile.displayName,
                            avatar: userProfile.photoURL
                        },
                        group: {
                            id: group.id,
                            name: group.name
                        }
                    });
                });
                alert("Request sent."); 
            }
            else {
                await db.collection('groups').doc(group.id).update({ members: arrayUnion(userProfile.uid), [`scores.${userProfile.uid}`]: 0 });
                // Notify admins of new member
                group.admins.forEach((adminId: string) => {
                    sendDirectNotification(adminId, {
                        type: 'system',
                        text: `${userProfile.displayName} joined your group "${group.name}".`,
                        fromUser: {
                            id: userProfile.uid,
                            name: userProfile.displayName,
                            avatar: userProfile.photoURL
                        }
                    });
                });
            }
        }
    };
    const requestToJoinGroup = async (gid: string) => {
        if (!userProfile || !db) return;
        const groupDoc = await db.collection('groups').doc(gid).get();
        if (!groupDoc.exists) return;
        const group = groupDoc.data() as GroupGame;
        
        await db.collection('groups').doc(gid).update({ joinRequests: arrayUnion(userProfile.uid) });
        
        // Notify admins
        group.admins.forEach((adminId: string) => {
            sendDirectNotification(adminId, {
                type: 'group_join_request',
                text: `requested to join your group "${group.name}".`,
                fromUser: {
                    id: userProfile.uid,
                    name: userProfile.displayName,
                    avatar: userProfile.photoURL
                },
                group: {
                    id: gid,
                    name: group.name
                }
            });
        });
    };
    const approveJoinRequest = async (gid: string, uid: string) => {
        if (!db) return;
        const groupDoc = await db.collection('groups').doc(gid).get();
        if (!groupDoc.exists) return;
        const group = groupDoc.data() as GroupGame;

        await db.collection('groups').doc(gid).update({ joinRequests: arrayRemove(uid), members: arrayUnion(uid), [`scores.${uid}`]: 0 });

        // Notify member
        sendDirectNotification(uid, {
            type: 'group_join_accepted',
            text: `Your request to join "${group.name}" was accepted!`,
            fromUser: userProfile ? {
                id: userProfile.uid,
                name: userProfile.displayName,
                avatar: userProfile.photoURL
            } : undefined,
            group: {
                id: gid,
                name: group.name
            }
        });
    };
    const declineJoinRequest = async (gid: string, uid: string) => {
        await db.collection('groups').doc(gid).update({ joinRequests: arrayRemove(uid) });
        // Optionally notify about decline, but often it's better to stay silent
    };
    const updateGroupScore = async (gid: string, p: number, pid: string) => {
        if (!userProfile || !db) return;
        
        const groupDoc = await db.collection('groups').doc(gid).get();
        if (!groupDoc.exists) return;
        const group = groupDoc.data() as GroupGame;
        
        const oldScore = group.scores[userProfile.uid] || 0;
        const newScore = oldScore + p;
        
        await db.collection('groups').doc(gid).update({ 
            [`scores.${userProfile.uid}`]: increment(p), 
            [`solvedPuzzles.${userProfile.uid}`]: arrayUnion(pid) 
        });

        // Group leaderboard pass check
        const membersPassed = group.members.filter(mId => {
            const mScore = group.scores[mId] || 0;
            return mId !== userProfile.uid && mScore >= oldScore && mScore < newScore;
        });

        membersPassed.forEach(mId => {
            sendDirectNotification(mId, {
                type: 'leaderboard_pass',
                text: `just passed you in the group "${group.name}" leaderboard!`,
                fromUser: {
                    id: userProfile.uid,
                    name: userProfile.displayName,
                    avatar: userProfile.photoURL
                },
                group: {
                    id: gid,
                    name: group.name
                }
            });
        });

        // Group Milestone check (every 50 points in a group)
        if (Math.floor(newScore / 50) > Math.floor(oldScore / 50)) {
            const milestone = Math.floor(newScore / 50) * 50;
            sendDirectNotification(userProfile.uid, {
                type: 'system',
                text: `🏆 Group Milestone! You reached ${milestone} points in "${group.name}"!`,
                fromUser: {
                    id: 'system',
                    name: 'What Was It?',
                    avatar: 'https://cdn-icons-png.flaticon.com/512/3112/3112946.png'
                },
                group: { id: gid, name: group.name }
            });
        }
    };
    const deductGroupPoints = async (gid: string, p: number) => userProfile && db.collection('groups').doc(gid).update({ [`scores.${userProfile.uid}`]: increment(-p) });
    const kickMember = async (gid: string, uid: string) => {
        if (!db) return;
        const groupDoc = await db.collection('groups').doc(gid).get();
        if (!groupDoc.exists) return;
        const group = groupDoc.data() as GroupGame;

        await db.collection('groups').doc(gid).update({ members: arrayRemove(uid), admins: arrayRemove(uid) });

        // Notify user
        sendDirectNotification(uid, {
            type: 'system', // Or group_kick if you add it to types
            text: `You were removed from the group "${group.name}".`,
            fromUser: userProfile ? {
                id: userProfile.uid,
                name: userProfile.displayName,
                avatar: userProfile.photoURL
            } : undefined
        });
    };
    const resetGroupScores = async (gid: string) => {
        const doc = await db.collection('groups').doc(gid).get();
        if (!doc.exists) return;
        const group = doc.data() as GroupGame;
        const zero: any = {};
        group.members.forEach(m => zero[m] = 0);
        await db.collection('groups').doc(gid).update({ scores: zero });
    };
    const registerFaceLogin = async (img: string) => user && db.collection('users').doc(user.uid).update({ isFaceLoginEnabled: true, faceLoginData: img });
    const disableFaceLogin = async () => user && db.collection('users').doc(user.uid).update({ isFaceLoginEnabled: false, faceLoginData: null });
    const loginWithFace = async (img: string) => allUsers.find(u => u.isFaceLoginEnabled && u.faceLoginData === img) || null;

    const useAICredit = async () => { if (!userProfile || userProfile.aiCredits <= 0) return false; await db.collection('users').doc(userProfile.uid).update({ aiCredits: increment(-1) }); return true; };
    const addAICredits = async (a: number) => user && db.collection('users').doc(user.uid).update({ aiCredits: increment(a) });
    const markOnboardingComplete = async () => user && db.collection('users').doc(user.uid).update({ hasCompletedOnboarding: true });
    
    const upgradeToBusiness = async () => {
        if (!user || !db) return;
        await db.collection('users').doc(user.uid).update({ 
            isBusiness: true,
            businessActionText: 'Visit Website'
        });
    };

    // Updated purchaseStoreItem to block native platforms
    const purchaseStoreItem = async (type: 'clue' | 'credit' | 'business', amount: number, cost: string) => {
        if (!user) return false;
        
        // COMPLIANCE: Disable Stripe on Native Mobile (Android/iOS)
        if (Capacitor.isNativePlatform()) {
            alert("Purchases are currently disabled in the mobile app. Please use the web version or watch ads to earn rewards.");
            return false;
        }

        try {
            const priceKey = `${type}_${amount}` as keyof typeof STRIPE_PRICE_IDS;
            const res = await functions.httpsCallable('createStripeCheckoutV2')({ priceId: STRIPE_PRICE_IDS[priceKey], itemType: type, amount, returnUrl: window.location.origin });
            if (res.data.url) { window.location.href = res.data.url; return true; }
            return false;
        } catch (e) { console.error(e); return false; }
    };

    const value: AuthContextType = {
        user, userProfile, loading, usersLoading, allUsers, groups, sentGroupRequests,
        login, signup, logout, resetPassword, updateSettings, updateUsername, addPoints, addPointsToUser, updateScore, deductPoints,
        useClue, addFreeClue, addCluesAfterNativeIAP, toggleFollow, toggleBlockUser, createGroup, updateGroup, deleteGroup, joinGame, requestToJoinGroup,
        approveJoinRequest, declineJoinRequest, updateGroupScore, deductGroupPoints, registerFaceLogin, disableFaceLogin, loginWithFace,
        useAICredit, addAICredits, markOnboardingComplete, kickMember, resetGroupScores, purchaseStoreItem, deleteAccount, upgradeToBusiness
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
